import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { suggestEmailCorrection } from '@/lib/emailSuggestion'
import { type EmailDeliverability, checkEmailDeliverability } from '@/services/emailValidationService'

// Only MX-check things that already look like a complete address, to avoid
// firing on every keystroke.
const COMPLETE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DEBOUNCE_MS = 600

type Props = {
  /** Current email value from the parent form. */
  readonly email: string
  /** Apply a corrected address back into the form field. */
  readonly onAccept: (corrected: string) => void
  readonly className?: string
}

/**
 * Advisory email hint shown beneath an email field. Suggests a correction for
 * domain typos (client-side, instant) and otherwise soft-warns when the domain
 * can't receive mail (server MX check, debounced). Never blocks submission.
 */
export function EmailFieldHint({ email, onAccept, className }: Props) {
  const { t } = useTranslation()
  const suggestion = useMemo(() => suggestEmailCorrection(email), [email])
  const [deliverability, setDeliverability] = useState<EmailDeliverability | null>(null)

  useEffect(() => {
    setDeliverability(null)
    const value = email.trim()
    // Skip the network check when we already have a typo suggestion (more
    // actionable) or the address doesn't look complete yet.
    if (suggestion || !COMPLETE_EMAIL.test(value)) return

    const controller = new AbortController()
    const timer = setTimeout(() => {
      checkEmailDeliverability(value, controller.signal).then((result) => {
        if (!controller.signal.aborted) setDeliverability(result)
      })
    }, DEBOUNCE_MS)

    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [email, suggestion])

  if (suggestion) {
    return (
      <p className={className ?? 'mt-1 text-sm text-amber-700'}>
        {t('emailHint.didYouMean', { suggested: suggestion })}{' '}
        <button
          type='button'
          onClick={() => onAccept(suggestion)}
          className='font-medium underline underline-offset-2 hover:text-amber-800'
        >
          {t('emailHint.useThis')}
        </button>
      </p>
    )
  }

  if (deliverability && !deliverability.deliverable) {
    return <p className={className ?? 'mt-1 text-sm text-amber-700'}>{t('emailHint.undeliverable')}</p>
  }

  return null
}
